/**
 * Model documents (`*.model.json`, `prism.model/1`): a tree of parts, each a
 * shape generator plus ops. See engine/crates/prism_model/src/schema.md.
 *
 * Parts are addressed two ways:
 *   - index paths (`[0, 2]`) for editing, and
 *   - prism part paths (`"body/arm#2"`) for display, selection that survives
 *     reloads, and for telling the AI which part is meant.
 * Everything here is pure: edits return a new document.
 */

import type { TransformDesc, Vec3 } from './scene-doc.js';

export interface ModelMaterial {
  color?: number[];
  roughness?: number;
  metallic?: number;
  emissive?: number[];
  /** Multiplies `emissive` (default 1; > 1 blooms). */
  emissiveIntensity?: number;
  /** Emissive image (project path, sRGB), multiplied by `emissive` × `emissiveIntensity`. */
  emissiveMap?: string;
  toon?: boolean;
  /** standard | toon | unlit | project `*.wgsl` custom shader. */
  shader?: string;
  /** Base color image (project path); painted textures are flattened here. */
  map?: string;
  normalMap?: string;
  metallicRoughnessMap?: string;
  /** Packed image: red = occlusion, green = roughness, blue = metallic. */
  ormMap?: string;
  /** Texture paint source layers, bottom first (schema.md "Texture painting & baking"). */
  layers?: PaintLayerDesc[];
  /** Texture paint protection mask (project PNG, red = protection). */
  paintMask?: string;
  /** Color space `layers` blend in: linear (default) | srgb (legacy look). */
  paintBlendSpace?: 'linear' | 'srgb';
  [extra: string]: unknown;
}

/** One texture paint layer of a material (`MaterialDesc::layers`). */
export interface PaintLayerDesc {
  name: string;
  /** Project PNG path. */
  image: string;
  /** normal (default) | multiply | add | subtract | screen | overlay | darken | lighten | color | softLight */
  blend?: string;
  /** 0..1, default 1. */
  opacity?: number;
  hidden?: boolean;
}

export interface ModelShape {
  type: string;
  [field: string]: unknown;
}

export interface ModelOp {
  op: string;
  /** Applied in the editor viewport (modifier eye); omitted when true. */
  viewport?: boolean;
  /** Applied on export and in game (modifier camera); omitted when true. */
  export?: boolean;
  [field: string]: unknown;
}

export interface ModelArray {
  count?: number;
  offset?: Vec3;
  rotation?: Vec3;
  scale?: number;
  radial?: { count?: number; axis?: string; radius?: number } | null;
}

export interface ModelPart {
  name?: string;
  material?: string;
  shape?: ModelShape;
  transform?: TransformDesc;
  ops?: ModelOp[];
  array?: ModelArray;
  mirror?: 'x' | 'y' | 'z';
  /** UVs of array / mirror copies: shared (default) or a separate tile per copy. */
  copyUvs?: 'shared' | 'unique';
  children?: ModelPart[];
  hidden?: boolean;
  /** Not pickable / movable in the viewport (editor only). */
  locked?: boolean;
  [extra: string]: unknown;
}

export interface ModelDoc {
  format?: string;
  name?: string;
  materials?: Record<string, ModelMaterial>;
  parts: ModelPart[];
  [extra: string]: unknown;
}

export type IndexPath = readonly number[];

export function parseModel(text: string): ModelDoc {
  const value: unknown = JSON.parse(text);
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    throw new Error('Model file is not a JSON object.');
  }
  const doc = value as ModelDoc;
  return { ...doc, parts: Array.isArray(doc.parts) ? doc.parts : [] };
}

/** Pretty JSON with arrays of numbers kept on one line (vertices, colors). */
export function stringifyCompact(value: unknown, indent = ''): string {
  if (Array.isArray(value)) {
    if (value.length === 0) return '[]';
    if (value.every((v) => typeof v === 'number')) return `[${value.map((v) => JSON.stringify(v)).join(', ')}]`;
    const inner = indent + '  ';
    return `[\n${value.map((v) => inner + stringifyCompact(v ?? null, inner)).join(',\n')}\n${indent}]`;
  }
  if (value !== null && typeof value === 'object') {
    const entries = Object.entries(value as Record<string, unknown>).filter(([, v]) => v !== undefined);
    if (entries.length === 0) return '{}';
    const inner = indent + '  ';
    return `{\n${entries.map(([k, v]) => `${inner}${JSON.stringify(k)}: ${stringifyCompact(v, inner)}`).join(',\n')}\n${indent}}`;
  }
  return JSON.stringify(value) ?? 'null';
}

export function serializeModel(doc: ModelDoc): string {
  return `${stringifyCompact(doc)}\n`;
}

export function newModel(name: string): ModelDoc {
  return {
    format: 'prism.model/1',
    name,
    materials: { default: { color: [0.8, 0.8, 0.8, 1], roughness: 0.8 } },
    parts: [
      {
        name: 'body',
        material: 'default',
        shape: { type: 'box', size: [1, 1, 1], bevel: 0.02 },
        transform: { position: [0, 0.5, 0] },
      },
    ],
  };
}

/** Segment names for a sibling list, exactly as prism's `segment_names`. */
export function segmentNames(siblings: readonly ModelPart[]): string[] {
  const seen = new Map<string, number>();
  return siblings.map((part, i) => {
    const name = part.name ?? '';
    if (!name) return `#${i}`;
    const n = (seen.get(name) ?? 0) + 1;
    seen.set(name, n);
    return n === 1 ? name : `${name}#${n}`;
  });
}

export function partAt(doc: ModelDoc, path: IndexPath): ModelPart | null {
  let list: readonly ModelPart[] = doc.parts;
  let found: ModelPart | null = null;
  for (const i of path) {
    found = list[i] ?? null;
    if (!found) return null;
    list = found.children ?? [];
  }
  return found;
}

/** Index path → prism part path (`body/arm#2`). */
export function partPathOf(doc: ModelDoc, path: IndexPath): string {
  const segments: string[] = [];
  let list: readonly ModelPart[] = doc.parts;
  for (const i of path) {
    const names = segmentNames(list);
    const name = names[i];
    if (name === undefined) break;
    segments.push(name);
    list = list[i]?.children ?? [];
  }
  return segments.join('/');
}

/** Prism part path → index path, or null when it does not resolve. */
export function resolvePartPath(doc: ModelDoc, partPath: string): number[] | null {
  const out: number[] = [];
  let list: readonly ModelPart[] = doc.parts;
  for (const segment of partPath.split('/').filter(Boolean)) {
    const i = segmentNames(list).indexOf(segment);
    if (i < 0) return null;
    out.push(i);
    list = list[i]?.children ?? [];
  }
  return out.length ? out : null;
}

export interface PartRow {
  part: ModelPart;
  path: number[];
  partPath: string;
  depth: number;
  hasChildren: boolean;
}

export function partRows(doc: ModelDoc, collapsed: ReadonlySet<string> = new Set()): PartRow[] {
  const out: PartRow[] = [];
  const walk = (list: readonly ModelPart[], prefix: number[], parentPath: string, depth: number) => {
    const names = segmentNames(list);
    list.forEach((part, i) => {
      const path = [...prefix, i];
      const partPath = parentPath ? `${parentPath}/${names[i]}` : names[i]!;
      const kids = part.children ?? [];
      out.push({ part, path, partPath, depth, hasChildren: kids.length > 0 });
      if (kids.length && !collapsed.has(partPath)) walk(kids, path, partPath, depth + 1);
    });
  };
  walk(doc.parts, [], '', 0);
  return out;
}

/** Copy-on-write update of the sibling list that contains `path`. */
function withSiblings(
  doc: ModelDoc,
  parentPath: IndexPath,
  update: (siblings: ModelPart[]) => ModelPart[],
): ModelDoc {
  if (parentPath.length === 0) return { ...doc, parts: update(doc.parts.slice()) };
  const rec = (list: readonly ModelPart[], depth: number): ModelPart[] => {
    const i = parentPath[depth]!;
    const copy = list.slice();
    const part = copy[i];
    if (!part) return copy;
    if (depth === parentPath.length - 1) {
      copy[i] = { ...part, children: update((part.children ?? []).slice()) };
    } else {
      copy[i] = { ...part, children: rec(part.children ?? [], depth + 1) };
    }
    return copy;
  };
  return { ...doc, parts: rec(doc.parts, 0) };
}

export function updatePart(
  doc: ModelDoc,
  path: IndexPath,
  update: (part: ModelPart) => ModelPart,
): ModelDoc {
  if (!path.length || !partAt(doc, path)) return doc;
  const index = path[path.length - 1]!;
  return withSiblings(doc, path.slice(0, -1), (siblings) => {
    siblings[index] = update(siblings[index]!);
    return siblings;
  });
}

/** Set (or with `undefined`, remove) one top-level field of a part. */
export function setPartField(doc: ModelDoc, path: IndexPath, key: string, value: unknown): ModelDoc {
  return updatePart(doc, path, (part) => {
    const next: ModelPart = { ...part };
    if (value === undefined || value === null || (key === 'hidden' && value === false)) {
      delete next[key];
    } else {
      next[key] = value;
    }
    return next;
  });
}

/** Append a part under `parent` (null = root). Returns the new index path. */
export function addPart(
  doc: ModelDoc,
  parent: IndexPath | null,
  part: ModelPart,
): { doc: ModelDoc; path: number[] } {
  const parentPath = parent ?? [];
  if (parentPath.length && !partAt(doc, parentPath)) return { doc, path: [] };
  let index = 0;
  const next = withSiblings(doc, parentPath, (siblings) => {
    index = siblings.length;
    return [...siblings, part];
  });
  return { doc: next, path: [...parentPath, index] };
}

export function deletePart(doc: ModelDoc, path: IndexPath): ModelDoc {
  if (!path.length || !partAt(doc, path)) return doc;
  const index = path[path.length - 1]!;
  return withSiblings(doc, path.slice(0, -1), (siblings) => siblings.filter((_, i) => i !== index));
}

/** Copy a part (and children) right after itself. Returns the copy's path. */
export function duplicatePart(doc: ModelDoc, path: IndexPath): { doc: ModelDoc; path: number[] } {
  const part = partAt(doc, path);
  if (!part) return { doc, path: [...path] };
  const index = path[path.length - 1]!;
  const copy = JSON.parse(JSON.stringify(part)) as ModelPart;
  const next = withSiblings(doc, path.slice(0, -1), (siblings) => {
    siblings.splice(index + 1, 0, copy);
    return siblings;
  });
  return { doc: next, path: [...path.slice(0, -1), index + 1] };
}

/** Move a part up (-1) or down (+1) among its siblings. Returns its new path. */
export function movePart(doc: ModelDoc, path: IndexPath, delta: -1 | 1): { doc: ModelDoc; path: number[] } {
  const index = path[path.length - 1];
  if (index === undefined || !partAt(doc, path)) return { doc, path: [...path] };
  const parentPath = path.slice(0, -1);
  const siblings = parentPath.length ? (partAt(doc, parentPath)?.children ?? []) : doc.parts;
  const target = index + delta;
  if (target < 0 || target >= siblings.length) return { doc, path: [...path] };
  const next = withSiblings(doc, parentPath, (list) => {
    const [moved] = list.splice(index, 1);
    list.splice(target, 0, moved!);
    return list;
  });
  return { doc: next, path: [...parentPath, target] };
}

export function setOps(doc: ModelDoc, path: IndexPath, ops: ModelOp[]): ModelDoc {
  return setPartField(doc, path, 'ops', ops.length ? ops : undefined);
}

export function moveOp(ops: readonly ModelOp[], index: number, delta: -1 | 1): ModelOp[] {
  const target = index + delta;
  if (target < 0 || target >= ops.length) return ops.slice();
  const next = ops.slice();
  const [moved] = next.splice(index, 1);
  next.splice(target, 0, moved!);
  return next;
}

export function setMaterial(doc: ModelDoc, name: string, material: ModelMaterial | null): ModelDoc {
  const materials = { ...(doc.materials ?? {}) };
  if (material === null) delete materials[name];
  else materials[name] = material;
  return { ...doc, materials };
}

/** Rename a material and repoint every part that used it. */
export function renameMaterial(doc: ModelDoc, from: string, to: string): ModelDoc {
  if (!to || from === to || !doc.materials?.[from] || doc.materials[to]) return doc;
  const materials: Record<string, ModelMaterial> = {};
  for (const [key, value] of Object.entries(doc.materials)) materials[key === from ? to : key] = value;
  const repoint = (parts: readonly ModelPart[]): ModelPart[] =>
    parts.map((p) => ({
      ...p,
      ...(p.material === from ? { material: to } : {}),
      ...(p.children ? { children: repoint(p.children) } : {}),
    }));
  return { ...doc, materials, parts: repoint(doc.parts) };
}

/** Default fields for each shape type, from the schema. */
export const SHAPE_DEFAULTS: Record<string, Record<string, unknown>> = {
  box: { size: [1, 1, 1], bevel: 0 },
  sphere: { radius: 0.5, segments: 24, rings: 16 },
  capsule: { radius: 0.25, height: 1, segments: 16, rings: 6 },
  cylinder: { radiusTop: 0.5, radiusBottom: 0.5, height: 1, segments: 24 },
  cone: { radius: 0.5, height: 1, segments: 24 },
  torus: { radius: 0.5, tube: 0.15, segments: 24, sides: 12 },
  plane: { size: [1, 1], subdivisions: 1 },
  lathe: { points: [[0, 0], [0.4, 0.1], [0.3, 0.8], [0, 1]], segments: 24, smooth: true },
  extrude: { polygon: [[-0.5, 0], [0.5, 0], [0, 0.8]], depth: 0.2, bevel: 0, smooth: false },
  tube: { path: [[0, 0, 0], [0.3, 0.5, 0], [0, 1, 0]], radius: 0.1, segments: 8, smooth: true },
  sweep: { rect: [0.1, 0.05, 0.01], path: [[0, 0, 0], [0, 0, -1]], scale: [], twist: 0, smooth: true, caps: true },
  loft: {
    sections: [
      { position: [0, 0, 0], rect: [0.4, 0.4, 0.08] },
      { position: [0, 0.5, 0], radius: 0.25 },
      { position: [0, 0.8, 0], radius: 0.08 },
    ],
    segments: 24,
    smooth: true,
    caps: true,
  },
  mesh: { vertices: [], faces: [], creases: [] },
  blob: { balls: [{ center: [0, 0, 0], radius: 0.5 }], resolution: 32, smoothness: 0.2 },
  sculpt: { items: [{ type: 'sphere', radius: 0.3 }], strokes: [], resolution: 128, blend: 0.02 },
  // Organic / vegetation shapes (hand editors in components/model/shapes).
  // No `smooth` here: it is an int on skin/garment but a bool on curves.
  tree: { preset: 'oak', seed: 0, height: 6 },
  skin: {
    nodes: [
      { name: 'body', position: [0, 0.5, 0], radius: 0.2 },
      { name: 'head', parent: 'body', position: [0, 0.9, 0], radius: 0.12 },
    ],
    resolution: 96,
  },
  hair: { scalp: { center: [0, 0, 0], radii: [0.08, 0.1, 0.1], guides: 32, length: 0.15 }, cards: 96, width: 0.02 },
  cloth: { outline: [[-0.4, 0], [0.4, 0], [0.5, 1], [-0.5, 1]] },
  garment: { target: '', region: { above: 0 }, offset: 0.006, looseness: 0.01 },
  empty: {},
};

export const OP_DEFAULTS: Record<string, Record<string, unknown>> = {
  subsurf: { levels: 2, creaseAngle: 180 },
  subdivide: { levels: 1, smooth: true },
  smooth: { iterations: 1, factor: 0.5 },
  bevel: { amount: 0.05 },
  noise: { amount: 0.05, scale: 2, seed: 0, octaves: 3 },
  twist: { degrees: 90, axis: 'y' },
  taper: { amount: 0.5, axis: 'y' },
  bend: { degrees: 45, axis: 'y' },
  mirror: { axis: 'x' },
  flatShade: {},
  inflate: { amount: 0.05 },
  boolean: { mode: 'subtract', with: '', method: 'exact', resolution: 48 },
  vertexColor: { color: [1, 1, 1, 1] },
  // Sculpt multiresolution (prism_model schema.md "Sculpting"); a `sculpt` op right after it sculpts at `sculptLevel`.
  multires: { levels: 1 },
  remesh: { mode: 'voxel', voxelSize: 0.05 },
  decimate: { mode: 'collapse', ratio: 0.5 },
};

/** Procedural texture patterns (prism_model texgen). */
export const TEXTURE_PATTERNS = [
  'wood',
  'planks',
  'brushed',
  'metal',
  'rust',
  'stone',
  'rock',
  'concrete',
  'bricks',
  'tiles',
  'fabric',
  'leather',
  'marble',
  'bark',
  'ground',
  'scratches',
  'skin',
  'hair',
  'scales',
  'leaf',
  'leafCluster',
  'needles',
  'needleSpray',
  'frond',
  'grassBlade',
  'petal',
  'fur',
  'hairStrand',
  'feather',
  'noise',
] as const;

export const TEXTURE_DEFAULTS: Record<string, unknown> = {
  pattern: 'noise',
  size: 0.5,
  scale: 1,
  contrast: 1,
  bump: 1,
  roughnessVariation: 0.2,
  wear: 0,
  scratches: 0,
  seed: 0,
  resolution: 512,
};

/** Change a part's shape type, keeping fields the new type shares. */
export function changeShapeType(shape: ModelShape | undefined, type: string): ModelShape {
  const defaults = SHAPE_DEFAULTS[type] ?? {};
  const next: ModelShape = { type, ...defaults };
  for (const key of Object.keys(defaults)) {
    if (shape && key in shape) next[key] = shape[key];
  }
  return next;
}

/** Count parts in the whole tree. */
export function countParts(parts: readonly ModelPart[]): number {
  return parts.reduce((n, p) => n + 1 + countParts(p.children ?? []), 0);
}
